import * as React from 'react';
import { Circle, Layer } from 'react-konva';
import Konva from 'konva';
import ModuleStore from '../../Module/_stores/ModuleStore';
import RateLimiter from '@af-common/Common/Utils/RateLimiter';
import * as ModuleResolver from '../../Module/_config/ModuleResolver';
import { DOCUMENT_DATA_DPI } from '../../_config/Consts';
import Dimensions, { D } from '../../Utils/Dimensions'

export type Props = {
	enabled: boolean
	module: ModuleStore
	moduleId: string
	updater?: Function
	documentOffsetX?: number
	documentOffsetY?: number
	documentWidth?: number
	documentHeight?: number
	documentDPI?: number
	shiftDown?: boolean
}

type Coords = {x: number, y: number, radius: number, rotation: number}
type Extra = {dashRotation: number}
export type State = Coords & Extra;

export default class Transformer extends React.Component<Props, State> {
	private radiusMod:number = 1.2;
	private animationRL:RateLimiter;
	private transformRL:RateLimiter;
	public ref:Konva.Layer;
	public prevScale:number = 1.0;

	state = { x: 0, y: 0, radius: 1, rotation: 0, dashRotation: 0 }

	componentDidMount(){
		this.moduleToState();
		this.transformRL = new RateLimiter(15);
		this.animationRL = new RateLimiter(150);
	}

	componentWillUnmount(){
		this.transformRL.stop();
		this.animationRL.stop();
	}

	componentDidUpdate(prevProps:Props, prevState:State){
		if(!this.props.module) return;

		this.animationRL.throttle(() => this.setState({dashRotation: (this.state.dashRotation + 300 / (2*Math.PI*Math.max(0.0001, this.state.radius))) % 360}));

		if(
			this.state.x != prevState.x || 
			this.state.y != prevState.y || 
			this.state.rotation != prevState.rotation || 
			this.state.radius != prevState.radius
		) this.stateToModule();
		else if(this.props != prevProps) this.moduleToState();
	}

	private getMaxRadius = () => {
		let module = this.props.module;

		if(ModuleResolver.hasDimensions(module) && ModuleResolver.hasRotation(module)){
			let max:D = Dimensions.getModuleMaxDimensions(module.get('width'), module.get('height'), this.props.documentWidth, this.props.documentHeight)
			let p = Math.sqrt(max.width * max.width + max.height * max.height)
			return (p * (this.props.documentDPI / DOCUMENT_DATA_DPI))/2 * this.radiusMod
		}

		return Math.min(this.props.documentWidth, this.props.documentHeight) * (this.props.documentDPI / DOCUMENT_DATA_DPI) * this.radiusMod
	}

	private moduleToState = () => {
		if(!this.props.module) {
			return;
		}

		let module = this.props.module;
		let newState:State = this.state;

		if(ModuleResolver.hasPosition(module)){
			newState.x = module.get('x');
			newState.y = module.get('y');
		}

		if(ModuleResolver.hasRotation(module)){
			newState.rotation = module.get('rotation');
		}

		if(ModuleResolver.hasDimensions(module)){
			newState.radius = (Math.sqrt(Math.pow(module.get('width'), 2) + Math.pow(module.get('height'), 2)) / 2) * this.radiusMod;
		}
		
		if(ModuleResolver.hasRadius(module)){
			newState.radius = module.get('radius') * this.radiusMod;
		}

		newState.x = newState.x * (this.props.documentDPI / DOCUMENT_DATA_DPI) + this.props.documentOffsetX;
		newState.y = newState.y * (this.props.documentDPI / DOCUMENT_DATA_DPI) + this.props.documentOffsetY;
		newState.radius *= (this.props.documentDPI / DOCUMENT_DATA_DPI);

		this.setState(newState as State);
	}

	private stateToModule = () => {
		let module = this.props.module;
		let update:any = {};

		if(ModuleResolver.hasPosition(module)){
			update.x = Math.round((this.state.x - this.props.documentOffsetX) / (this.props.documentDPI / DOCUMENT_DATA_DPI));
			update.y = Math.round((this.state.y - this.props.documentOffsetY) / (this.props.documentDPI / DOCUMENT_DATA_DPI));
		}

		if(ModuleResolver.hasRotation(module)){
			update.rotation = this.state.rotation;
		}

		if(ModuleResolver.hasDimensions(module)){
			let ratio:number = Math.pow(module.get('width'), 2) / Math.pow(module.get('height'), 2);
			let h:number = Math.pow(((this.state.radius/(this.props.documentDPI / DOCUMENT_DATA_DPI)) / this.radiusMod ) * 2, 2) / (ratio + 1);
			let width = Math.round(Math.sqrt(h*ratio));
			let height = Math.round(Math.sqrt(h));
			if(width) update.width = width;
			if(height) update.height = height;
		}
		
		if(ModuleResolver.hasRadius(module)){
			update.radius = (this.state.radius/(this.props.documentDPI / DOCUMENT_DATA_DPI)) / this.radiusMod;
		}

		for(let value in update){
			if(!update.hasOwnProperty(value)) continue;
			update[value] = Math.floor(update[value] * 100) / 100;
		}

		this.transformRL.throttle(() => {
			this.props.updater(this.props.moduleId, update)
		});
	}

	private onDragMove = (e:Konva.KonvaEventObject<DragEvent>) => {
		this.setState({
			x: this.ref.x(),
			y: this.ref.y(),
		});
	}

	private onHandleStart = (e:Konva.KonvaEventObject<DragEvent>) => {
		e.cancelBubble = true;
		this.prevScale = this.state.radius;
	}

	private onHandleMove = (e:Konva.KonvaEventObject<DragEvent>) => {
		e.cancelBubble = true;
		e.target.x(this.state.radius);
		e.target.y(0);

		let targetX:number, targetY:number;

		//Touch events
		if(e.evt.touches) {
			//Stop here if not touches
			if(!e.evt.touches[0]) {
				return;
			}

			targetX = e.evt.touches[0].clientX;
			targetY = e.evt.touches[0].clientY;
		} else {
			//Mouse events
			targetX = e.evt.clientX;
			targetY = e.evt.clientY;
		}

		let position = {
			x: targetX - this.state.x,
			y: targetY - this.state.y - 65,
		}
		
		let rotation = Math.atan2(position.y, position.x) * (180/Math.PI) 

		if (!this.props.shiftDown) {
			rotation = Math.round(rotation / 15) * 15
		}

		this.setState({
			rotation: rotation,
			radius: Math.min(this.getMaxRadius(), Math.sqrt(Math.pow(position.x, 2) + Math.pow(position.y, 2)))
		})
	}

	private onHandleMouseEnter = (e:Konva.KonvaEventObject<MouseEvent>) => {e.target.fill("#EEEEEE")};
	private onHandleMouseLeave = (e:Konva.KonvaEventObject<MouseEvent>) => {e.target.fill("#FFFFFF")};

	private onTransformerMouseEnter = () => (this.ref.getParent() as Konva.Stage).container().style.cursor = "move";
	private onTransformerMouseLeave = () => (this.ref.getParent() as Konva.Stage).container().style.cursor = "default";

	render = () => ( this.props.enabled &&
		<Layer
			ref={ref => this.ref = ref}
			draggable
			x={this.state.x}
			y={this.state.y}
			onDragMove={(e:Konva.KonvaEventObject<DragEvent>) => this.onDragMove(e)}
			onMouseEnter={this.onTransformerMouseEnter} 
			onMouseLeave={this.onTransformerMouseLeave}
			rotation={this.state.rotation}
		>
			<Circle 
				name="bounds"
				stroke="#FFFFFF"
				strokeWidth={2}
				dash={[4,4]}
				rotation={this.state.dashRotation}
				radius={this.state.radius}
			/>
			<Circle 
				name="bounds"
				stroke="#000000"
				strokeWidth={2}
				dash={[4,4]}
				rotation={this.state.dashRotation + 360*(4/(2*Math.PI*this.state.radius))}
				radius={this.state.radius}
			/>
			<Circle 
				name="handle"
				radius={15}
				draggable
				stroke='#222222'
				strokeWidth={0.75}
				fill="#FFFFFF"
				x={this.state.radius}
				onMouseEnter={(e:Konva.KonvaEventObject<MouseEvent>) => this.onHandleMouseEnter(e)}
				onMouseLeave={(e:Konva.KonvaEventObject<MouseEvent>) => this.onHandleMouseLeave(e)}
				onDragStart={(e:Konva.KonvaEventObject<DragEvent>) => this.onHandleStart(e)}
				onDragMove={(e:Konva.KonvaEventObject<DragEvent>) => this.onHandleMove(e)}
			/>
		</Layer>
	)
}